/** The longest URL the API accepts as a store URL, and so as an allowed URL of a merchant. */
export const MAX_URL_LENGTH = 50;

/**
 * Whether the value is an https:// base URL of at most MAX_URL_LENGTH characters, as the API requires for the allowed
 * URLs of a merchant: scheme and host only (a trailing slash is fine), no path, query or fragment.
 */
export const isHttpsBaseUrl = (value: string): boolean => {
    if (value.length > MAX_URL_LENGTH) return false;

    try {
        const url = new URL(value);
        return url.protocol === 'https:' && url.pathname === '/' && !url.search && !url.hash;
    } catch {
        return false;
    }
};

const splitLines = (text: string | undefined) => (text ?? '').split(/\r?\n/);

/** The lines of a textarea as a list of URLs: trimmed, without blank lines and without duplicates. */
export const parseUrlLines = (text: string | undefined): string[] => {
    const urls = splitLines(text)
        .map(line => line.trim())
        .filter(line => line.length > 0);

    return Array.from(new Set(urls));
};

export const requiredUrlLinesMessage = 'Required field';

export const invalidUrlLineMessage = (lineNumber: number) =>
    `Line ${lineNumber} is not an https:// address without a path, such as https://shop.example.se (at most ${MAX_URL_LENGTH} characters).`;

/**
 * react-hook-form validate rule for a textarea with one base URL per line. Blank lines are fine; the first
 * line that is not an https:// base URL is reported.
 */
export const validateUrlLines = (text: string | undefined): true | string => {
    const invalidIndex = splitLines(text).findIndex(line => {
        const trimmed = line.trim();
        return trimmed.length > 0 && !isHttpsBaseUrl(trimmed);
    });

    return invalidIndex === -1 || invalidUrlLineMessage(invalidIndex + 1);
};

/**
 * react-hook-form validate rule for a textarea that must hold at least one base URL, one per line. Whitespace-only text
 * counts as empty, so an empty field is reported before an invalid line.
 */
export const validateRequiredUrlLines = (text: string | undefined): true | string =>
    parseUrlLines(text).length === 0 ? requiredUrlLinesMessage : validateUrlLines(text);
